import { Card, CardContent } from "@/components/ui/card";
import { Brain, Hand, HeartPulse, Activity, Bone, Moon } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const symptoms = [
  { icon: Brain, label: "Verlust erlernter Fähigkeiten", desc: "Kinder verlieren Fähigkeiten, die sie bereits gelernt haben" },
  { icon: Hand, label: "Motorische Einschränkungen", desc: "Gezielte Bewegungen und Greifen werden unmöglich" },
  { icon: HeartPulse, label: "Epileptische Anfälle", desc: "Häufig auftretende Krampfanfälle" },
  { icon: Activity, label: "Atemstörungen", desc: "Unregelmäßige Atmung und Hyperventilation" },
  { icon: Bone, label: "Skoliose", desc: "Verkrümmung der Wirbelsäule" },
  { icon: Moon, label: "Schlafprobleme", desc: "Gestörter Schlaf-Wach-Rhythmus" },
];

export default function RettInfo() {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [cardRef, cardVisible] = useScrollAnimation({ threshold: 0.15 });
  const [gridRef, gridVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="rett-info" data-testid="rett-info-section" className="py-20 md:py-28" style={{ background: "#F8F9FA" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 max-w-3xl mx-auto animate-fade-up ${headerVisible ? 'visible' : ''}`}
        >
          <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>
            Aufklärung
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ color: "#1F2937" }}>
            Was ist das Rett-Syndrom?
          </h2>
          <div className="section-divider mx-auto mb-8" />
          <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
            Das Rett-Syndrom ist eine seltene neurologische Entwicklungsstörung, die fast
            ausschließlich Mädchen betrifft. Viele Kinder entwickeln sich zunächst scheinbar normal.
            Doch meist ab dem <strong>18. Lebensmonat</strong> verlieren sie Fähigkeiten, die sie
            bereits gelernt haben – wie Sprechen, Greifen und gezielte Bewegungen.
          </p>
        </div>

        {/* Gene info card */}
        <div 
          ref={cardRef}
          className={`max-w-2xl mx-auto mb-12 animate-scale ${cardVisible ? 'visible' : ''}`}
        >
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
            <CardContent className="p-6 md:p-8 text-center">
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
                Die Ursache ist meist eine Mutation im <strong style={{ color: "#b46eb6" }}>MECP2-Gen</strong>.
                Dieses Gen ist entscheidend für die Entwicklung und Funktion von Nervenzellen im Gehirn.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Symptoms grid */}
        <div className="mb-8 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-2" style={{ color: "#1F2937" }}>
            Typische Symptome
          </h3>
        </div>
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {symptoms.map((item, idx) => {
            const Icon = item.icon;
            return (
              <Card
                key={idx}
                data-testid={`symptom-card-${idx}`}
                className={`border border-gray-100 shadow-sm hover:shadow-md transition-all bg-white group card-animate ${gridVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-6 flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors"
                    style={{ background: "rgba(149, 225, 187, 0.5)" }}
                  >
                    <Icon className="w-6 h-6" style={{ color: "#b46eb6" }} />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm md:text-base mb-1" style={{ color: "#1F2937" }}>
                      {item.label}
                    </h4>
                    <p className="text-sm" style={{ color: "#9CA3AF" }}>
                      {item.desc}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
